import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const NetworkContext = createContext({ isOnline: true, connectionQuality: 'good' });

export const NetworkProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  // 'good' | 'slow' | 'offline'
  const [connectionQuality, setConnectionQuality] = useState(navigator.onLine ? 'good' : 'offline');
  const failCountRef = useRef(0);
  const successCountRef = useRef(0);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Start optimistic — degrade only if requests actually fail
      setConnectionQuality('good');
      failCountRef.current = 0;
      successCountRef.current = 0;
    };
    const handleOffline = () => {
      setIsOnline(false);
      setConnectionQuality('offline');
      failCountRef.current = 0;
      successCountRef.current = 0;
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Called by authenticatedFetch on each request outcome
  const reportRequestOutcome = useCallback((success) => {
    if (!navigator.onLine) return;

    if (success) {
      successCountRef.current++;
      failCountRef.current = Math.max(0, failCountRef.current - 1);
      // 2 consecutive successes → good
      if (successCountRef.current >= 2) {
        setConnectionQuality('good');
      }
    } else {
      failCountRef.current++;
      successCountRef.current = 0;
      // 3 consecutive failures → slow
      if (failCountRef.current >= 3) {
        setConnectionQuality('slow');
      }
    }
  }, []);

  return (
    <NetworkContext.Provider value={{ isOnline, connectionQuality, reportRequestOutcome }}>
      {children}
    </NetworkContext.Provider>
  );
};

export const useNetwork = () => useContext(NetworkContext);
